import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../utils/trpc';
import EditIcon from '../images/EditIcon.png';
import TagManagerModal from './TagManagerModal';
import styles from '../styles/GroupCard.module.css';
import { supabase } from '../utils/supabase';

interface Tag {
  id: number;
  name: string;
}

interface GroupCardProps {
  group: {
    id: string;
    name: string;
    description?: string;
    privacy?: 'public' | 'private';
    role?: 'admin' | 'member' | 'owner' | string; // from membership
    banner_url?: string;
    profile_picture_url?: string;
  };
  canEdit?: boolean;
}

const GroupCard = ({ group, canEdit = false }: GroupCardProps) => {
  const navigate = useNavigate();
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [galleryUrls, setGalleryUrls] = useState<string[]>([]);

  // Fetch tags
  const { data: groupTags, refetch: refetchGroupTags } = trpc.tags.getGroupTags.useQuery(
    { groupId: group.id },
    { enabled: !!group.id, staleTime: 0 },
  );

  const currentTags: Tag[] = useMemo(() => {
    if (!groupTags || !Array.isArray(groupTags)) return [];
    return groupTags.map((entry: any) => ({
      id: entry.tag_id,
      name: entry.tags?.name ?? 'Unknown',
    }));
  }, [groupTags]);

  const handleModalClose = async () => {
    setIsTagModalOpen(false);
    await refetchGroupTags();
  };

  // Fetch gallery images
  const { data: galleryImages } = trpc.group.getImages.useQuery(
    { groupId: group.id },
    { enabled: !!group.id },
  );

  useEffect(() => {
    if (!galleryImages) {
      setGalleryUrls([]);
      return;
    }
    const urls = galleryImages
      .map((img: any) => {
        const { data } = supabase.storage.from('groups').getPublicUrl(img.storage_path);
        return data?.publicUrl ? `${data.publicUrl}?t=${Date.now()}` : '';
      })
      .filter(Boolean);
    setGalleryUrls(urls);
  }, [galleryImages]);

  const avatarInitial = group.name?.charAt(0).toUpperCase() ?? 'G';

  return (
    <div className={styles.cardContainer}>
      <div className={styles.card}>
        {/* Hero Banner */}
        {group.banner_url ? (
          <div className={styles.bannerContainer}>
            <img
              onLoad={(e) => e.currentTarget.classList.add('loaded')}
              src={group.banner_url}
              alt="Group Banner"
              className={styles.bannerImage}
            />
          </div>
        ) : (
          <div className={styles.heroBanner} />
        )}
        <div className={styles.cardContent}>
          {/* Group Info */}
          <div className={styles.cardInfo}>
            <div className={styles.header}>
              {group.profile_picture_url ? (
                <img
                  onLoad={(e) => e.currentTarget.classList.add('loaded')}
                  src={group.profile_picture_url}
                  alt={group.name}
                  className={styles.groupAvatar}
                />
              ) : (
                <div className={styles.groupAvatar}>{avatarInitial}</div>
              )}

              <h3 className={styles.name}>{group.name}</h3>
            </div>

            <p className={styles.description}>
              {group.description || 'No description provided'}
            </p>

            {group.privacy && (
              <div className={styles.privacy}>
                <span>{group.privacy === 'private' ? 'Private' : 'Public'}</span>
              </div>
            )}

            {/* Gallery */}
            <div className={styles.gallerySection}>
              <h2 className={styles.galleryTitle}>Gallery</h2>
              {galleryUrls.length > 0 ? (
                <div className={styles.gallery}>
                  {galleryUrls.map((url, index) => (
                    <div key={index} className={styles.galleryItem}>
                      <img
                        onLoad={(e) => e.currentTarget.classList.add('loaded')}
                        src={url}
                        alt="Group Gallery"
                        className={styles.galleryImage}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.emptyGallery}>
                  <span className={styles.emptyIcon}>🖼️</span>
                  <p className={styles.emptyText}>No photos yet</p>
                </div>
              )}
            </div>

            {/* Tags */}
            <div className={styles.tagsSection}>
              <div className={styles.sectionHeaderContainer}>
                <h3 className={styles.sectionHeader}>Interests</h3>
                {canEdit && (
                  <button
                    data-testid="editTagsButton"
                    className={styles.editTagsButton}
                    onClick={() => setIsTagModalOpen(true)}
                  >
                    <img
                      onLoad={(e) => e.currentTarget.classList.add('loaded')}
                      src={EditIcon}
                      alt="Edit"
                      className={styles.editIcon}
                    />
                  </button>
                )}
              </div>

              <div className={styles.tagsContainer}>
                {currentTags.length === 0 ? (
                  <p>No interests added yet.</p>
                ) : (
                  currentTags.map((tag) => (
                    <span key={tag.id} className={styles.tag}>
                      {tag.name}
                    </span>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className={styles.cardActions}>
            <button
              className={styles.viewBtn}
              onClick={() => navigate(`/groups/${group.id}`)}
            >
              View
            </button>
            <button
              className={styles.viewGroupEventsBtn}
              onClick={() => navigate(`/groups/${group.id}/events`)}
            >
              View Events
            </button>

            {canEdit && (
              <button
                className={styles.editBtn}
                onClick={() => navigate(`/groups/${group.id}/edit`)}
              >
                Edit
              </button>
            )}
          </div>
        </div>
      </div>

      {canEdit && (
        <TagManagerModal
          isOpen={isTagModalOpen}
          onClose={handleModalClose}
          mode="edit"
          min={1}
          max={10}
          initialTags = {currentTags}
          originalTags={currentTags}
          profileType="group"
          groupId={group.id}
        />
      )}
    </div>
  );
};

export default GroupCard;
